import { EuiButton, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiSpacer, EuiSuperSelect, EuiTitle } from '@elastic/eui';
import React, { useCallback } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { outletSettingsV4 } from '../../../../api/outlet/outletSettingsAPI';
import useFormActionsHandler from '../../../../hooks/useFormActionsHandler';
import useOutletFormValues from '../../Hooks/useOutletFormValues';
import './CategoryLayout.css';

const CategoryLayout = () => {
    const dispatch = useDispatch();

    const updateFormValues = useCallback((data) => {
        return {
            ...{
                category_list_display_settings: data.category_list_display_settings,
                category_display_mode: data.category_display_mode,
                outlet_screen_display_configurations: data.outlet_screen_display_configurations,
                best_seller_display_mode: data?.settings?.best_seller_display_mode,
                featured_item_display_mode: data?.settings?.featured_item_display_mode,
            },
        };
    }, []);

    const { defaultValues, setDefaultValues, onSuccessCallback } = useOutletFormValues({ updateFormValues });

    const onFormSaveApi = useCallback(
        async (data) => {
            const response = await dispatch(
                outletSettingsV4('layout_settings', {
                    category_list_display_settings: data.category_list_display_settings,
                    category_display_mode: data.category_display_mode,
                    best_seller_display_mode: data.best_seller_display_mode,
                    featured_item_display_mode: data.featured_item_display_mode,
                })
            );
            if (response.success) setDefaultValues(updateFormValues(response.outlet));
            return response;
        },
        [dispatch]
    );

    const { control, setValue, watch } = useFormActionsHandler({
        onFormSaveApi,
        defaultValues,
        onSuccessCallback,
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'category_list_display_settings',
    });

    const handleAddRow = useCallback(() => {
        append({ column_rank: fields?.length + 1, category_display_count: 1 });
        setValue('', '', { shouldDirty: true });
    }, [append, fields?.length, setValue]);

    const handleRemoveRow = useCallback(
        (idx) => {
            remove(idx);
            let updatedDisplaySettings = [];
            if (watch('category_list_display_settings')?.length) {
                for (let [index, setting] of watch('category_list_display_settings')?.entries()) {
                    setting.column_rank = index + 1;
                    updatedDisplaySettings.push(setting);
                }
            }
            setValue('category_list_display_settings', updatedDisplaySettings, { shouldDirty: true });
        },
        [remove, setValue, watch]
    );

    return (
        <>
            <EuiFlexItem grow={false} style={{ display: 'flex', maxWidth: 400 }}>
                <EuiTitle size='xs'>
                    <h4>Select Category View</h4>
                </EuiTitle>
                <EuiSpacer size='s' />
                <Controller
                    control={control}
                    name='category_display_mode'
                    render={({ field }) => (
                        <EuiSuperSelect
                            options={categoryViewOptions}
                            valueOfSelected={field.value}
                            onChange={field.onChange}
                        />
                    )}
                />
            </EuiFlexItem>
            <EuiSpacer />
            <EuiFlexGroup>
                <EuiFlexItem style={{ maxWidth: 400 }}>
                    <EuiTitle size='xs'>
                        <h4>Best Seller Display View</h4>
                    </EuiTitle>
                    <EuiSpacer size='s' />
                    <Controller
                        control={control}
                        name='best_seller_display_mode'
                        render={({ field }) => (
                            <EuiSuperSelect
                                options={bestSellerDisplayOptions}
                                valueOfSelected={field.value}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </EuiFlexItem>
                <EuiFlexItem style={{ maxWidth: 400 }}>
                    <EuiTitle size='xs'>
                        <h4>Featured Item Display View</h4>
                    </EuiTitle>
                    <EuiSpacer size='s' />
                    <Controller
                        control={control}
                        name='featured_item_display_mode'
                        render={({ field }) => (
                            <EuiSuperSelect
                                options={featuredDisplayOptions}
                                valueOfSelected={field.value}
                                onChange={field.onChange}
                            />
                        )}
                    />
                </EuiFlexItem>
            </EuiFlexGroup>
            <EuiSpacer />

            {fields?.map((field, index) => {
                return (
                    <React.Fragment key={field.id.toString()}>
                        <span>
                            <EuiFlexItem>
                                <EuiTitle size='xs'>
                                    <h4>
                                        Row{' '}
                                        {field?.row_rank
                                            ? field.row_rank
                                            : field.column_rank
                                                ? field.column_rank
                                                : null}
                                    </h4>
                                </EuiTitle>
                            </EuiFlexItem>
                            <EuiFlexGroup alignItems='flexEnd'>
                                <EuiFlexItem>
                                    <EuiFormRow label='Category Display Count'>
                                        <Controller
                                            control={control}
                                            name={`category_list_display_settings[${index}].category_display_count`}
                                            rules={{ required: 'Please Select Category Display Count' }}
                                            render={({ field }) => {
                                                return (
                                                    <EuiSuperSelect
                                                        onChange={field.onChange}
                                                        valueOfSelected={field.value}
                                                        options={rowOptions}
                                                    />
                                                );
                                            }}
                                        />
                                    </EuiFormRow>
                                </EuiFlexItem>
                                <EuiFlexItem grow={false}>
                                    {' '}
                                    <EuiButton onClick={() => handleRemoveRow(index)}>Remove</EuiButton>
                                </EuiFlexItem>
                            </EuiFlexGroup>
                        </span>
                        <EuiSpacer />
                    </React.Fragment>
                );
            })}

            <EuiButton fill onClick={handleAddRow}>
                Add Row
            </EuiButton>
        </>
    );
};

const bestSellerDisplayOptions = [
    { value: 'small_card', inputDisplay: 'Small Card' },
    { value: 'big_card', inputDisplay: 'Big Card' },
];

const featuredDisplayOptions = [
    { value: 'list', inputDisplay: 'List' },
    { value: 'big_card', inputDisplay: 'Big Card' },
];

const categoryViewOptions = [
    { value: 'overlay', inputDisplay: 'Overlay View' },
    { value: 'card', inputDisplay: 'Card View' },
];

const rowOptions = [
    { value: 1, inputDisplay: '1' },
    { value: 2, inputDisplay: '2' },
    { value: 3, inputDisplay: '3' },
];
export default React.memo(CategoryLayout);
